import React from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'

import { useMode } from '../../../../../../../hooks/useMode'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../../../../utils/helper/validateFormikFields'
import { useConfigContext } from '../../../../../../ui-config'
import { CompanyCode, CustomFieldDefinition } from '../../../../suppliers'
import { FormFieldName, FormValues } from '../common'
import { EditCompanyCodeView } from './EditCompanyCode.view'

type Props = {
  handleSubmit: (formData: any, resetForm: any) => Promise<void>
  isRequestRunning: boolean
  handleClose: () => void
  companyCode: CompanyCode | undefined
  customFieldsOptions?: CustomFieldDefinition[]
  allCompanyCodes?: CompanyCode[]
}

export const EditCompanyCodeForm: React.FC<Props> = ({
  handleSubmit,
  isRequestRunning,
  handleClose,
  companyCode,
  customFieldsOptions,
  allCompanyCodes,
}) => {
  const { t } = useTranslation()
  const { uiConfig: config } = useConfigContext()
  const mode = useMode()

  // Helper function to get the correct config based on mode
  const getModeConfig = () => {
    const masterDataConfig =
      config.selfservices.masterDataManagement.companyCodes
    switch (mode) {
      case 'add':
        return masterDataConfig.add
      case 'info':
        return masterDataConfig.info
      case 'edit':
      default:
        return masterDataConfig.edit
    }
  }

  const modeConfig = getModeConfig()

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      id: companyCode?.Id || '',
      isStandard: companyCode?.IsStandard || false,
      displayId: companyCode?.Common?.DisplayId || '',
      company: companyCode?.Common?.Company || '',
      salesTaxID: companyCode?.Common?.SalesTaxID || '',
      taxNumber: companyCode?.Common?.TaxNumber || '',
      street: companyCode?.Address?.Street || '',
      postalCode: companyCode?.Address?.PostalCode || '',
      city: companyCode?.Address?.City || '',
      country: companyCode?.Address?.Country || 'Germany',
      customFields: companyCode?.CustomFields || [],
    },
    validate: formData => {
      let errors: FormikErrors<FormValues> = {}

      const fieldsToValidate: FieldToValidate[] = [
        {
          fieldName: 'displayId',
          config: modeConfig.displayId,
        },
        {
          fieldName: 'company',
          config: modeConfig.company,
        },
        {
          fieldName: 'taxNumber',
          config: modeConfig.taxNumber,
        },
        {
          fieldName: 'salesTaxID',
          config: modeConfig.salesTaxID,
        },
        {
          fieldName: 'street',
          config: modeConfig.street,
        },
        {
          fieldName: 'postalCode',
          config: modeConfig.postalCode,
        },
        {
          fieldName: 'city',
          config: modeConfig.city,
        },
        {
          fieldName: 'country',
          config: modeConfig.country,
        },
      ]

      validateFormikFields(fieldsToValidate, formData, errors)

      if (!formData.displayId) {
        errors.displayId = t(
          'masterDataManagement.packagingData.table.edit.dialog.form.packagingDataId.validation.required',
        )
      }

      if (!formData.company) {
        errors.company = t(
          'masterDataManagement.packagingData.table.edit.dialog.form.packagingDataId.validation.required',
        )
      }

      // Note: isStandard validation is not needed since the field is automatically set programmatically
      // and cannot be manually changed by the user

      formData.customFields?.forEach((customField, index) => {
        const fieldErrors: any = {}

        if (!customField.CustomFieldDefinition.Key) {
          fieldErrors.CustomFieldDefinition = {
            Key: t(
              'masterDataManagement.packagingData.table.edit.dialog.form.description.validation.required',
            ),
          }
        }

        if (
          customField.Value === null ||
          customField.Value === undefined ||
          customField.Value === ''
        ) {
          fieldErrors.Value = t(
            'masterDataManagement.packagingData.table.edit.dialog.form.description.validation.required',
          )
        }

        // Only initialize and set errors if there are any
        if (Object.keys(fieldErrors).length > 0) {
          if (!errors.customFields) {
            errors.customFields = []
          }
          ;(errors.customFields as any)[index] = fieldErrors
        }
      })

      return errors
    },
    onSubmit: async formData => {
      await handleSubmit(formData, formik.resetForm)
    },
  })

  const isFormFieldValid = (name: FormFieldName) =>
    !(formik.touched[name] && formik.errors[name])

  const getFormErrorMessage = (name: FormFieldName) => {
    const error = formik.errors[name]

    const errorMessage =
      typeof error === 'string' ? error : JSON.stringify(error)
    return (
      !isFormFieldValid(name) && (
        <small className="p-error">{errorMessage}</small>
      )
    )
  }

  const getNestedFormErrorMessage = (index: number, fieldName: string) => {
    const customFieldErrors = formik.errors.customFields as any
    const customFieldTouched = formik.touched.customFields as any

    // Handle nested field names like 'CustomFieldDefinition.Key'
    const fieldPath = fieldName.split('.')
    let hasError = customFieldErrors?.[index]
    let isTouched = customFieldTouched?.[index]

    // Navigate through the nested path
    for (const pathSegment of fieldPath) {
      hasError = hasError?.[pathSegment]
      isTouched = isTouched?.[pathSegment]
    }

    if (hasError && isTouched) {
      return <small className="p-error">{hasError}</small>
    }

    return false
  }

  const availableCustomFieldsOptions = customFieldsOptions?.filter(
    customField =>
      !formik.values.customFields.some(
        assignedCustomField => assignedCustomField.Id === customField.Id,
      ),
  )

  return (
    <EditCompanyCodeView
      formik={formik}
      isFormFieldValid={isFormFieldValid}
      getFormErrorMessage={getFormErrorMessage}
      getNestedFormErrorMessage={getNestedFormErrorMessage}
      config={modeConfig}
      handleClose={handleClose}
      isRequestRunning={isRequestRunning}
      availableCustomFieldsOptions={availableCustomFieldsOptions}
      allCompanyCodes={allCompanyCodes}
    />
  )
}
